/**
 * Curriculum & Objective Formatting Helpers
 */

/**
 * Clean up learner display name (e.g. fixes casing quirks like "STudent N" -> "Student N")
 *
 * @param {string} rawName - Raw learner name
 * @param {boolean} isArabic - Language flag
 * @returns {string} Cleaned learner name
 */
export const cleanLearnerName = (rawName, isArabic = false) => {
  if (!rawName) return isArabic ? 'الطالب' : 'The student';
  const trimmed = String(rawName).trim();
  if (trimmed.startsWith('STudent')) {
    return 'Student' + trimmed.slice(7);
  }
  return trimmed || (isArabic ? 'الطالب' : 'The student');
};

/**
 * Format curriculum objective text to replace first-person "I can..." with the student's name
 * e.g. "I can understand..." -> "Student N can understand..."
 * or in Arabic "أستطيع أن..." -> "Student N يستطيع أن..."
 *
 * @param {string} text - Original objective text
 * @param {string} learnerName - Student/learner name
 * @param {boolean} isArabic - Language flag
 * @returns {string} Objective text personalized with the student's name
 */
export const formatObjectiveWithStudentName = (text, learnerName, isArabic = false) => {
  if (!text || typeof text !== 'string') return text || '';
  const name = cleanLearnerName(learnerName, isArabic);

  if (isArabic) {
    if (text.startsWith('أستطيع أن ')) {
      return `${name} يستطيع أن ${text.slice(10)}`;
    }
    if (text.startsWith('أعرف أنّ ')) {
      return `${name} يعرف أنّ ${text.slice(9)}`;
    }
    if (text.startsWith('أستطيع ')) {
      return `${name} يستطيع ${text.slice(7)}`;
    }
    return text;
  }

  // English: replace "I can [verb]" with "[Student Name] can [verb]"
  if (/^I can\b/i.test(text)) {
    return text.replace(/^I can\b/i, `${name} can`);
  }
  // English: replace "I know [that]" with "[Student Name] knows [that]"
  if (/^I know\b/i.test(text)) {
    return text.replace(/^I know\b/i, `${name} knows`);
  }
  // English: replace generic starting "I " with "[Student Name] "
  if (/^I\b/i.test(text)) {
    return text.replace(/^I\b/i, name);
  }

  return text;
};
